import React, { useState } from 'react';

export default function PoolMap({ value, onChange, readOnly = false }) {
  const [hovered, setHovered] = useState(null);

  const handleClick = (e) => {
    if (readOnly) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 800);
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 500);
    onChange({ zone: 'libre', x, y, label: 'Posicion marcada' });
  };

  const Pin = ({ x, y }) => (
    <g transform={`translate(${x - 14}, ${y - 42})`} filter="url(#pin-shadow)" data-testid="pool-pin">
      <path
        d="M14,0 C6.3,0 0,6.3 0,14 C0,24.5 14,42 14,42 C14,42 28,24.5 28,14 C28,6.3 21.7,0 14,0 Z"
        fill="#48293A"
        stroke="white"
        strokeWidth="2.5"
      />
      <circle cx="14" cy="13" r="5.5" fill="white" />
    </g>
  );

  return (
    <div data-testid="pool-map-container">
      {!readOnly && (
        <p className="text-sm text-manrique-grey mb-3">
          Haz clic en el mapa para marcar la ubicacion
        </p>
      )}
      <div className="relative overflow-hidden rounded-2xl border border-black/5 shadow-sm">
        <svg
          viewBox="0 0 800 500"
          className={`w-full h-auto ${readOnly ? '' : 'cursor-crosshair'}`}
          onClick={handleClick}
          data-testid="pool-map-svg"
        >
          <defs>
            {/* Water gradient */}
            <linearGradient id="waterGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8AD4EC" />
              <stop offset="100%" stopColor="#5AAFCC" />
            </linearGradient>
            <linearGradient id="waterGradCascada" x1="0.2" y1="0" x2="0.8" y2="1">
              <stop offset="0%" stopColor="#8AD4EC" />
              <stop offset="100%" stopColor="#5AAFCC" />
            </linearGradient>
            <linearGradient id="waterGradInfantil" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#96DCED" />
              <stop offset="100%" stopColor="#6BBCD6" />
            </linearGradient>
            {/* Map shadow */}
            <filter id="map-shadow" x="-5%" y="-5%" width="110%" height="110%">
              <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#4C4D57" floodOpacity="0.08" />
            </filter>
            {/* Pool inner shadow for depth */}
            <filter id="pool-depth" x="-5%" y="-5%" width="110%" height="115%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="6" result="blur" />
              <feOffset in="blur" dx="0" dy="3" result="offset" />
              <feComposite in="offset" in2="SourceGraphic" operator="atop" result="shadow" />
              <feComponentTransfer in="shadow" result="darken">
                <feFuncA type="linear" slope="0.2" />
              </feComponentTransfer>
              <feMerge>
                <feMergeNode in="SourceGraphic" />
                <feMergeNode in="darken" />
              </feMerge>
            </filter>
            {/* Pin shadow */}
            <filter id="pin-shadow" x="-30%" y="-10%" width="160%" height="130%">
              <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#4C4D57" floodOpacity="0.25" />
            </filter>
            {/* Label backdrop */}
            <filter id="label-halo" x="-10%" y="-20%" width="120%" height="140%">
              <feFlood floodColor="#E0CDAA" floodOpacity="0.85" result="bg" />
              <feMorphology in="SourceAlpha" radius="3" operator="dilate" result="expanded" />
              <feComposite in="bg" in2="expanded" operator="in" result="halo" />
              <feMerge>
                <feMergeNode in="halo" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Background */}
          <rect width="800" height="500" fill="#FAFAF5" />

          {/* ═══ PARK OUTLINE ═══ */}
          <path
            d="M55,35 C100,12 200,8 310,14
               C380,18 430,10 490,18 C540,24 575,12 620,30
               C660,42 710,28 745,58 C768,78 782,115 778,155
               C774,190 756,208 748,235 C740,258 752,285 758,320
               C762,350 752,372 730,392 C710,408 680,418 650,428
               C610,440 550,446 470,450 C400,454 330,452 260,448
               C190,442 120,438 80,418 C50,400 38,370 32,325
               C26,275 30,225 34,175 C38,125 42,70 55,35 Z"
            fill="#E0CDAA"
            stroke="#E0CDAA"
            strokeWidth="1.5"
            filter="url(#map-shadow)"
          />

          {/* ═══ ARCHITECTURAL DETAILS ═══ */}
          {/* Trees - organic clusters */}
          {[
            [440, 90], [460, 80], [450, 105],
            [480, 170], [500, 160], [490, 185],
            [440, 210], [455, 225], [430, 230],
            [140, 380], [165, 395], [120, 395],
            [300, 410], [325, 420], [280, 425],
            [700, 150], [720, 170], [730, 140],
            [700, 340], [720, 355], [690, 360],
            [550, 400], [570, 415], [540, 420],
          ].map(([cx, cy], i) => (
            <circle key={`tree-${i}`} cx={cx} cy={cy} r={7 + (i % 3) * 2} fill="#27AE60" opacity={0.12 + (i % 3) * 0.05} stroke="#27AE60" strokeWidth="0.5" strokeOpacity="0.15" />
          ))}

          {/* Loungers - along pool edges */}
          {[
            [75, 375, 0], [95, 375, 0], [115, 375, 0],
            [340, 375, 0], [360, 375, 0], [380, 375, 0],
            [560, 280, -20], [575, 295, -20], [590, 310, -20],
            [680, 280, 15], [695, 295, 15], [710, 310, 15],
          ].map(([x, y, r], i) => (
            <g key={`lounger-${i}`} transform={`translate(${x},${y}) rotate(${r})`}>
              <rect x="-4" y="-10" width="8" height="20" rx="2" fill="white" stroke="#E0CDAA" strokeWidth="0.6" opacity="0.6" />
            </g>
          ))}

          {/* Umbrellas */}
          {[
            [85, 360], [105, 360], [350, 360], [370, 360],
            [565, 265], [695, 265],
          ].map(([cx, cy], i) => (
            <circle key={`umb-${i}`} cx={cx} cy={cy} r={5} fill={i % 2 === 0 ? '#48293A' : '#48293A'} opacity="0.15" stroke="white" strokeWidth="0.5" />
          ))}

          {/* ═══ PISCINA PRINCIPAL (U-shape) ═══ */}
          {/* Coping (white border effect) */}
          <path
            d="M82,100 C90,55 135,38 175,36
               C215,34 248,40 270,50 C292,40 325,34 365,36
               C405,38 450,55 458,100
               L455,325 C455,355 430,372 395,375
               C360,378 335,370 315,355 C305,347 298,335 295,320
               L292,220 C290,200 280,190 265,188
               C250,186 240,195 238,215
               L235,320 C232,335 225,347 215,355
               C195,370 170,378 135,375
               C100,372 85,355 85,325 Z"
            fill="white"
            stroke="none"
          />
          {/* Water fill */}
          <path
            d="M88,105 C95,62 138,46 175,44
               C213,42 245,47 268,56 C291,47 323,42 361,44
               C398,46 441,62 448,105
               L445,320 C445,348 424,362 392,365
               C360,368 338,362 320,350 C312,344 306,334 303,322
               L300,218 C298,195 285,182 268,180
               C251,178 238,190 236,213
               L233,322 C230,334 224,344 216,350
               C198,362 176,368 144,365
               C112,362 95,348 95,320 Z"
            fill="url(#waterGrad)"
            stroke="#4A9CB8"
            strokeWidth="1"
            filter="url(#pool-depth)"
          />
          {/* Water reflections */}
          <path d="M115,150 Q155,142 195,150" fill="none" stroke="white" strokeWidth="1.2" opacity="0.2" strokeLinecap="round" />
          <path d="M115,240 Q155,232 190,240" fill="none" stroke="white" strokeWidth="1" opacity="0.15" strokeLinecap="round" />
          <path d="M340,150 Q380,142 420,150" fill="none" stroke="white" strokeWidth="1.2" opacity="0.2" strokeLinecap="round" />
          <path d="M345,250 Q375,242 410,250" fill="none" stroke="white" strokeWidth="1" opacity="0.15" strokeLinecap="round" />

          {/* Island between arms */}
          <ellipse cx="268" cy="55" rx="14" ry="7" fill="#DDD5C4" stroke="#E0CDAA" strokeWidth="0.8" opacity="0.7" />

          {/* Labels */}
          <g filter="url(#label-halo)">
            <text x="155" y="205" textAnchor="middle" fill="#4C4D57" fontSize="10" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.15em" opacity="0.85">PISCINA</text>
            <text x="155" y="218" textAnchor="middle" fill="#4C4D57" fontSize="10" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.15em" opacity="0.85">PRINCIPAL</text>
          </g>
          <g filter="url(#label-halo)">
            <text x="378" y="205" textAnchor="middle" fill="#4C4D57" fontSize="10" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.15em" opacity="0.85">PISCINA</text>
            <text x="378" y="218" textAnchor="middle" fill="#4C4D57" fontSize="10" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.15em" opacity="0.85">PRINCIPAL</text>
          </g>

          {/* ═══ PISCINA INFANTIL ═══ */}
          {/* Coping */}
          <path
            d="M448,320 C455,298 478,286 505,290
               C532,294 550,314 548,338
               C546,362 528,378 505,380
               C482,382 460,370 452,350
               C446,338 443,330 448,320 Z"
            fill="white" stroke="none"
          />
          {/* Water */}
          <path
            d="M454,322 C460,303 480,293 504,296
               C528,299 544,316 542,336
               C540,356 524,370 504,372
               C484,374 466,364 458,348
               C452,337 450,332 454,322 Z"
            fill="url(#waterGradInfantil)"
            stroke="#4A9CB8"
            strokeWidth="1"
            filter="url(#pool-depth)"
          />
          <path d="M475,320 Q495,315 515,320" fill="none" stroke="white" strokeWidth="1" opacity="0.2" strokeLinecap="round" />
          <g filter="url(#label-halo)">
            <text x="500" y="335" textAnchor="middle" fill="#4C4D57" fontSize="9" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.12em" opacity="0.85">PISCINA</text>
            <text x="500" y="347" textAnchor="middle" fill="#4C4D57" fontSize="9" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.12em" opacity="0.85">INFANTIL</text>
          </g>

          {/* ═══ PISCINA DE LA CASCADA ═══ */}
          {/* Coping */}
          <path
            d="M572,155 C590,105 645,82 700,92
               C750,102 775,145 770,190
               C765,235 735,265 695,278
               C655,290 610,280 588,255
               C566,230 558,195 572,155 Z"
            fill="white" stroke="none"
          />
          {/* Water */}
          <path
            d="M578,158 C595,112 647,90 698,99
               C745,108 768,148 763,188
               C758,228 730,256 693,268
               C656,280 615,272 595,250
               C575,228 565,198 578,158 Z"
            fill="url(#waterGradCascada)"
            stroke="#4A9CB8"
            strokeWidth="1"
            filter="url(#pool-depth)"
          />
          <path d="M615,160 Q660,150 705,160" fill="none" stroke="white" strokeWidth="1.2" opacity="0.2" strokeLinecap="round" />
          <path d="M608,210 Q650,200 695,210" fill="none" stroke="white" strokeWidth="1" opacity="0.15" strokeLinecap="round" />
          <g filter="url(#label-halo)">
            <text x="670" y="178" textAnchor="middle" fill="#4C4D57" fontSize="10" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.12em" opacity="0.85">PISCINA DE LA</text>
            <text x="670" y="192" textAnchor="middle" fill="#4C4D57" fontSize="10" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.12em" opacity="0.85">CASCADA</text>
          </g>

          {/* ═══ ENTRADA PRINCIPAL ═══ */}
          <g>
            <polygon points="400,430 392,445 408,445" fill="#4C4D57" opacity="0.85" />
            <text x="400" y="462" textAnchor="middle" fill="#4C4D57" fontSize="9" fontWeight="bold" fontFamily="sans-serif" letterSpacing="0.15em" opacity="0.7">ENTRADA PRINCIPAL</text>
          </g>

          {/* Pin */}
          {value && <Pin x={value.x} y={value.y} />}
        </svg>
      </div>
      {!readOnly && value && (
        <div className="mt-3 flex items-center gap-2 text-sm" data-testid="pool-location-info">
          <svg width="16" height="22" viewBox="0 0 28 42">
            <path d="M14,0 C6.3,0 0,6.3 0,14 C0,24.5 14,42 14,42 C14,42 28,24.5 28,14 C28,6.3 21.7,0 14,0 Z" fill="#48293A" />
            <circle cx="14" cy="13" r="5.5" fill="white" />
          </svg>
          <span className="font-medium text-manrique-black">Ubicacion marcada</span>
        </div>
      )}
    </div>
  );
}
